import React, {useEffect, useState} from 'react';
import {Button, Dropdown, Image, message, Modal, Space, Table, Tooltip, Input} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import '../styles/FormulaList.css';
import apiService from "../services/api";
import Search from "antd/es/input/Search";

const FormulaList = () => {
    const [formulas, setFormulas] = useState([]);
    const [selectedColor, setSelectedColor] = useState('全部/all');
    const [searchText, setSearchText] = useState('');
    const [colorTypes, setColorTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        pageCount: 0
    });
    const navigate = useNavigate();

    const [jumpPage, setJumpPage] = useState('');

    // 初始化加载颜色类型
    useEffect(() => {
        const fetchColorTypes = async () => {
            try {
                const response = await apiService.formula.getColorType();
                if (response.code === 200) {
                    setColorTypes([
                        {label: "全部/all", key: String(0)},
                        ...response.data.map((item, index) => ({
                            label: item,
                            key: String(index + 1),
                        })),
                    ]);
                }
            } catch (error) {
                message.error('获取颜色类型失败: ' + (error.message || '未知错误'));
            }
        };

        fetchColorTypes();
        fetchFormulas();
    }, []);

    const menuProps = {
        items: colorTypes,
        onClick: (item) => {
            const result = colorTypes.find(value => item.key === value.key);
            setSelectedColor(result?.label || '全部/all');
            fetchFormulas(null, null, result?.label, searchText, true);
        },
    };

    // 获取配方列表
    const fetchFormulas = async (pageNum, pageSize, colorType, keyword, resetPage = false) => {
        setLoading(true);
        try {
            const response = await apiService.formula.getByPage(
                resetPage ? 1 : pageNum || pagination.current,
                pageSize || pagination.pageSize,
                colorType === '请选择' || colorType === "全部/all" || !colorType ? '' : colorType,
                keyword || searchText,
            );

            if (response.code === 200) {
                setPagination({
                    current: response.data.current,
                    pageSize: response.data.size,
                    total: response.data.total,
                    pageCount: response.data.pages
                });
                setFormulas(response.data.records);
            }
        } catch (error) {
            message.error('获取配方列表失败: ' + (error.message || '未知错误'));
        } finally {
            setLoading(false);
        }
    };

    const onSearch = (searchText) => {
        setSearchText(searchText);
        fetchFormulas(null, null, selectedColor, searchText, true);
    }

    const onChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        if (!value) {
            fetchFormulas(null, null, selectedColor, '', true);
        }
    }

    const handleAddFormula = () => {
        navigate('/formulas/add');
    };

    const handleEdit = (record) => {
        navigate(`/formulas/edit/${record.id}`);
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '确认删除配方',
            content: `您确定要删除配方 "${record.name}" 吗？此操作不可逆。`,
            okText: '确认删除',
            cancelText: '取消',
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await apiService.formula.delete(record.id);
                    if (response.code === 200) {
                        message.success('配方删除成功');
                        fetchFormulas();
                    } else {
                        message.error('删除失败: ' + (response.message || '未知错误'));
                    }
                } catch (error) {
                    message.error('删除失败: ' + (error.message || '未知错误'));
                }
            }
        });
    };

    // 处理页码跳转
    const handleJumpPage = (e) => {
        const value = e.target.value;
        // 只允许输入数字
        if (/^\d*$/.test(value)) {
            setJumpPage(value);
        }
    };

    // 执行页码跳转
    const executeJumpPage = () => {
        if (!jumpPage) return;

        const pageNum = parseInt(jumpPage, 10);
        if (pageNum < 1 || pageNum > pagination.pageCount) {
            message.error(`请输入有效的页码 (1-${pagination.pageCount})`);
            return;
        }

        fetchFormulas(pageNum, pagination.pageSize, selectedColor, searchText);
        setJumpPage('');
    };

    // 处理回车键跳转
    const handleJumpPageKeyDown = (e) => {
        if (e.key === 'Enter') {
            executeJumpPage();
        }
    };

    const columns = [
        {
            title: '色板类型',
            dataIndex: 'colorType',
            key: 'colorType',
        },
        {
            title: '分类名称',
            dataIndex: 'categoryName',
            key: 'categoryName',
        },
        {
            title: '配方名称',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '配方编码',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: '配方图片',
            key: 'iconPath',
            dataIndex: 'iconPath',
            width: 400,
            render: (_, record) => (
                <div className="formula-image-preview" style={{width: '100%'}}>
                    {record.iconPaths && record.iconPaths.length > 0 ? (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            justifyContent: 'center'
                        }}>
                            {record.iconPaths.map((imageSrc, index) => (
                                <Image
                                    key={index}
                                    height={120}
                                    width={120}
                                    src={imageSrc}
                                    alt={`配方预览${index + 1}`}
                                    style={{
                                        objectFit: 'cover',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '4px'
                                    }}
                                    onError={(e) => console.error('图片加载失败', e)}
                                />
                            ))}
                        </div>
                    ) : record.iconPath ? (
                        <Image
                            height={120}
                            width={120}
                            src={record.iconPath}
                            alt="配方预览"
                            style={{
                                objectFit: 'cover',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px'
                            }}
                            onError={(e) => console.error('图片加载失败', e)}
                        />
                    ) : (
                        <span>暂无图片</span>
                    )}
                </div>
            ),
        },
        {
            title: '添加时间',
            dataIndex: 'createTime',
            key: 'createTime',
            render: (timestamp) => {
                const date = new Date(timestamp);
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
            },
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title="编辑">
                        <Button
                            type="primary"
                            shape="circle"
                            icon={<EditOutlined/>}
                            size="small"
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title="删除">
                        <Button
                            type="primary"
                            danger
                            shape="circle"
                            icon={<DeleteOutlined/>}
                            size="small"
                            onClick={() => handleDelete(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="formula-list-container">
            <div className="formula-list-header">
                <h2>配方管理</h2>
            </div>

            <div className={'formula-search-header'}>
                <Space size={0}>
                    <Dropdown.Button
                        className='dropdown-button'
                        overlayClassName="scrollable-menu"
                        autoFocus={true}
                        menu={menuProps}
                        placement="bottom">
                        {selectedColor}
                    </Dropdown.Button>

                    <Search
                        placeholder="请输入配方名称关键词"
                        onSearch={onSearch}
                        onChange={onChange}
                        style={{marginLeft: '5px', width: 200}}
                        allowClear
                    />
                </Space>

                <Button
                    type="primary"
                    icon={<PlusOutlined/>}
                    onClick={handleAddFormula}
                    className={'add-formula-button'}
                >
                    添加配方
                </Button>
            </div>

            <Table
                className="formula-table" // 添加特定类名
                columns={columns}
                dataSource={formulas}
                loading={loading}
                rowKey="id"
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        fetchFormulas(page, pageSize, selectedColor, searchText);
                    },
                    onShowSizeChange: (current, size) => {
                        fetchFormulas(current, size, selectedColor, searchText);
                    },
                    showTotal: (total, range) => (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ marginRight: 8 }}>
                                共 {total} 条记录 第 {pagination.current}/{pagination.pageCount} 页
                            </span>
                            <Space size={4}>
                                <span>跳至</span>
                                <Input
                                    style={{ width: 60 }}
                                    value={jumpPage}
                                    onChange={handleJumpPage}
                                    onKeyDown={handleJumpPageKeyDown}
                                    placeholder="页码"
                                />
                                <span>页</span>
                                <Button
                                    size="small"
                                    onClick={executeJumpPage}
                                    disabled={!jumpPage}
                                >
                                    跳转
                                </Button>
                            </Space>
                        </div>
                    ),
                }}
            />
        </div>
    );
};

export default FormulaList;
